import { z } from "zod";

import {
  dateString,
  endDateAfterStart,
  optionalText,
  requiredDate,
  requiredText
} from "@/lib/forms/schema";

import type {
  CreateForeignNationalImmigrationEntryExitHistoryEntryRequest,
  CreateForeignNationalImmigrationStatusHistoryEntryRequest,
  ForeignNationalImmigrationEntryExitHistoryEntry,
  ForeignNationalImmigrationStatusHistoryEntry,
  ImmigrationEntryExitEventType
} from "../../../../api/types/foreignNationalImmigrationHistory";
import { normalizeText, toDateInputValue } from "../../lib/format";
import { byDateDesc } from "../../lib/records";

export type TimelineFilter = "all" | "statuses" | "travel";

// Status entries ------------------------------------------------------------

export type StatusDraft = {
  status: string;
  filingDate: string;
  approvalDate: string;
  validityStartDate: string;
  validityExpirationDate: string;
  durationOfStatus: boolean;
  receiptNumber: string;
  employer: string;
  evidenceDocumentId: string;
  isCurrent: boolean;
  notes: string;
};

export type StatusPayload =
  CreateForeignNationalImmigrationStatusHistoryEntryRequest;

export const emptyStatusDraft = (): StatusDraft => ({
  status: "",
  filingDate: "",
  approvalDate: "",
  validityStartDate: "",
  validityExpirationDate: "",
  durationOfStatus: false,
  receiptNumber: "",
  employer: "",
  evidenceDocumentId: "",
  isCurrent: false,
  notes: ""
});

export const statusEntryToDraft = (
  e: ForeignNationalImmigrationStatusHistoryEntry
): StatusDraft => ({
  status: normalizeText(e.status),
  filingDate: toDateInputValue(e.filing_date),
  approvalDate: toDateInputValue(e.approval_date),
  validityStartDate: toDateInputValue(e.validity_start_date),
  validityExpirationDate: toDateInputValue(e.validity_expiration_date),
  durationOfStatus: Boolean(e.duration_of_status),
  receiptNumber: normalizeText(e.receipt_number),
  employer: normalizeText(e.employer),
  evidenceDocumentId: normalizeText(e.evidence_document_id),
  isCurrent: Boolean(e.is_current),
  notes: normalizeText(e.notes)
});

export const statusDraftsEqual = (a: StatusDraft, b: StatusDraft): boolean =>
  normalizeText(a.status) === normalizeText(b.status) &&
  normalizeText(a.filingDate) === normalizeText(b.filingDate) &&
  normalizeText(a.approvalDate) === normalizeText(b.approvalDate) &&
  normalizeText(a.validityStartDate) === normalizeText(b.validityStartDate) &&
  normalizeText(a.validityExpirationDate) ===
    normalizeText(b.validityExpirationDate) &&
  a.durationOfStatus === b.durationOfStatus &&
  normalizeText(a.receiptNumber) === normalizeText(b.receiptNumber) &&
  normalizeText(a.employer) === normalizeText(b.employer) &&
  a.evidenceDocumentId === b.evidenceDocumentId &&
  a.isCurrent === b.isCurrent &&
  normalizeText(a.notes) === normalizeText(b.notes);

/** "eac 21 900 12345" -> "EAC2190012345". */
export const receiptKey = (v: string): string =>
  v.replace(/[^a-z0-9]/gi, "").toUpperCase();

/** A status and when it became valid. */
export const statusDraftSchema: z.ZodType<StatusDraft, StatusDraft> =
  endDateAfterStart(
    z.object({
      status: requiredText("Status"),
      filingDate: dateString(),
      approvalDate: dateString(),
      validityStartDate: requiredDate("Valid from"),
      validityExpirationDate: dateString(),
      durationOfStatus: z.boolean(),
      receiptNumber: z
        .string()
        .refine(
          (v) => !v.trim() || /^[A-Z]{3}\d{10}$/.test(receiptKey(v)),
          "A receipt number is 3 letters and 10 digits (EAC2190012345)."
        ),
      employer: optionalText(),
      evidenceDocumentId: optionalText(),
      isCurrent: z.boolean(),
      notes: optionalText()
    }),
    "validityStartDate",
    "validityExpirationDate",
    "Expiration can't be before the valid-from date."
  );

export const statusDraftToPayload = (d: StatusDraft): StatusPayload => ({
  status: normalizeText(d.status),
  filing_date: normalizeText(d.filingDate) || null,
  approval_date: normalizeText(d.approvalDate) || null,
  validity_start_date: normalizeText(d.validityStartDate),
  validity_expiration_date: d.durationOfStatus
    ? null
    : normalizeText(d.validityExpirationDate) || null,
  duration_of_status: d.durationOfStatus,
  receipt_number: receiptKey(d.receiptNumber) || null,
  employer: normalizeText(d.employer) || null,
  evidence_document_id: normalizeText(d.evidenceDocumentId) || null,
  is_current: d.isCurrent,
  notes: normalizeText(d.notes) || null
});

export const sortStatusEntries =
  byDateDesc<ForeignNationalImmigrationStatusHistoryEntry>(
    (e) => e.validity_start_date
  );

export const statusFocusDomId = (id: string): string =>
  `immigration-status-${id}`;

// Travel (entry / exit) events ---------------------------------------------

export type TravelDraft = {
  eventType: ImmigrationEntryExitEventType;
  eventDate: string;
  portOfEntry: string;
  country: string;
  classOfAdmission: string;
  i94RecordNumber: string;
  i94ExpirationDate: string;
  notes: string;
};

export type TravelPayload =
  CreateForeignNationalImmigrationEntryExitHistoryEntryRequest;

export const emptyTravelDraft = (
  eventType: ImmigrationEntryExitEventType = "ENTRY"
): TravelDraft => ({
  eventType,
  eventDate: "",
  portOfEntry: "",
  country: "",
  classOfAdmission: "",
  i94RecordNumber: "",
  i94ExpirationDate: "",
  notes: ""
});

export const travelEntryToDraft = (
  e: ForeignNationalImmigrationEntryExitHistoryEntry
): TravelDraft => ({
  eventType: e.event_type,
  eventDate: toDateInputValue(e.event_date),
  portOfEntry: normalizeText(e.port_of_entry),
  country: normalizeText(e.country),
  classOfAdmission: normalizeText(e.class_of_admission),
  i94RecordNumber: normalizeText(e.i94_record_number),
  i94ExpirationDate: toDateInputValue(e.i94_expiration_date),
  notes: normalizeText(e.notes)
});

export const travelDraftsEqual = (a: TravelDraft, b: TravelDraft): boolean =>
  a.eventType === b.eventType &&
  normalizeText(a.eventDate) === normalizeText(b.eventDate) &&
  normalizeText(a.portOfEntry) === normalizeText(b.portOfEntry) &&
  normalizeText(a.country) === normalizeText(b.country) &&
  normalizeText(a.classOfAdmission) === normalizeText(b.classOfAdmission) &&
  normalizeText(a.i94RecordNumber) === normalizeText(b.i94RecordNumber) &&
  normalizeText(a.i94ExpirationDate) === normalizeText(b.i94ExpirationDate) &&
  normalizeText(a.notes) === normalizeText(b.notes);

/** Only the event date is required. */
export const travelDraftSchema: z.ZodType<TravelDraft, TravelDraft> = z.object({
  eventType: z.enum(["ENTRY", "EXIT"]) as z.ZodType<
    ImmigrationEntryExitEventType,
    ImmigrationEntryExitEventType
  >,
  eventDate: requiredDate("Event date"),
  portOfEntry: optionalText(),
  country: optionalText(),
  classOfAdmission: optionalText(),
  i94RecordNumber: optionalText(),
  i94ExpirationDate: dateString(),
  notes: optionalText()
});

export const travelDraftToPayload = (d: TravelDraft): TravelPayload => ({
  event_type: d.eventType,
  event_date: normalizeText(d.eventDate),
  port_of_entry: normalizeText(d.portOfEntry) || null,
  country: normalizeText(d.country) || null,
  class_of_admission: normalizeText(d.classOfAdmission) || null,
  i94_record_number: normalizeText(d.i94RecordNumber) || null,
  i94_expiration_date: normalizeText(d.i94ExpirationDate) || null,
  notes: normalizeText(d.notes) || null
});

export const sortTravelEntries =
  byDateDesc<ForeignNationalImmigrationEntryExitHistoryEntry>(
    (e) => e.event_date
  );

export const travelFocusDomId = (id: string): string =>
  `immigration-travel-${id}`;

// Merged timeline -----------------------------------------------------------

export type TimelineItem =
  | {
      kind: "status";
      id: string;
      sortDate: string;
      entry: ForeignNationalImmigrationStatusHistoryEntry;
    }
  | {
      kind: "travel";
      id: string;
      sortDate: string;
      entry: ForeignNationalImmigrationEntryExitHistoryEntry;
    };

export const buildTimeline = (
  statuses: ForeignNationalImmigrationStatusHistoryEntry[],
  travel: ForeignNationalImmigrationEntryExitHistoryEntry[],
  filter: TimelineFilter
): TimelineItem[] => {
  const items: TimelineItem[] = [];
  if (filter !== "travel") {
    statuses.forEach((e) =>
      items.push({
        kind: "status",
        id: e.immigration_status_history_id,
        sortDate: e.validity_start_date ?? "",
        entry: e
      })
    );
  }
  if (filter !== "statuses") {
    travel.forEach((e) =>
      items.push({
        kind: "travel",
        id: e.immigration_entry_exit_history_id,
        sortDate: e.event_date ?? "",
        entry: e
      })
    );
  }
  return items.sort(byDateDesc<TimelineItem>((i) => i.sortDate));
};
